import { Suspense, lazy, useState, type FC, type ReactNode } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { theme } from './theme';
import { AppLayout } from './components/layout';
import {
  ErrorBoundary,
  LoadingState,
  NotificationStack,
  OfflineBanner,
  SkipLink,
} from './components/common';
import { AppProvider } from './store';
import { AuthProvider, ReauthDialog, useAuth } from './auth';
import {
  SsoCallback,
  redirectToLogin,
  useSsoCheck,
} from '@codespeak11/cognito-passwordless';

// Lazy-loaded pages for code splitting
const LoginPage = lazy(() =>
  import('./pages/Login').then((m) => ({ default: m.LoginPage }))
);
const Dashboard = lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard }))
);
const TouchpointsPage = lazy(() =>
  import('./pages/Touchpoints').then((m) => ({ default: m.TouchpointsPage }))
);
const TouchpointWizard = lazy(() =>
  import('./pages/Touchpoints').then((m) => ({ default: m.TouchpointWizard }))
);
const JourneysPage = lazy(() =>
  import('./pages/Journeys').then((m) => ({ default: m.JourneysPage }))
);
const JourneyDesigner = lazy(() =>
  import('./pages/Journeys').then((m) => ({ default: m.JourneyDesigner }))
);
const ProjectPage = lazy(() =>
  import('./pages/Projects').then((m) => ({ default: m.ProjectPage }))
);
const EditProjectPage = lazy(() =>
  import('./pages/Projects').then((m) => ({ default: m.EditProjectPage }))
);
const PersonasPage = lazy(() =>
  import('./pages/Personas').then((m) => ({ default: m.PersonasPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/Analytics').then((m) => ({ default: m.AnalyticsPage }))
);
const OptimizationPage = lazy(() =>
  import('./pages/Optimization').then((m) => ({ default: m.OptimizationPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/Settings').then((m) => ({ default: m.SettingsPage }))
);

// Page loading fallback
const PageLoader = () => <LoadingState message="Loading..." variant="skeleton" rows={5} />;

// Auth loading fallback
const AuthLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

// Protected route wrapper - redirects to login if not authenticated
const ProtectedRoute: FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AuthLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// SSO callback route - exchanges the auth code for tokens, then routes the
// user back to wherever they were (preserved via `returnTo` by the SDK).
//
// onError renders an inline "Try Again" panel rather than navigating to
// /login. Per spa-auth-integration.md, navigating away on callback errors
// can loop (the /login page itself can re-trigger a silent check, which
// fails the same way, which fires onLoginRequired → /login again).
const SsoCallbackRoute: FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
          <Typography variant="h6">Sign-in didn&apos;t complete</Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => redirectToLogin()}
          >
            Try Again
          </Button>
          <Button onClick={() => navigate('/login', { replace: true })}>Cancel</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <SsoCallback
      onSuccess={(returnPath) => navigate(returnPath || '/', { replace: true })}
      onLoginRequired={() => navigate('/login', { replace: true })}
      onError={(err) => {
        // eslint-disable-next-line no-console
        console.error('[sso] callback error:', err);
        setError(err || 'Something went wrong while signing you in.');
      }}
    >
      <AuthLoader />
    </SsoCallback>
  );
};

// Silent SSO detection - if the user already has a session on
// auth.braindance.net, sign them in transparently.
//
// Per spa-auth-integration.md, useSsoCheck() must NEVER run on /login or
// /auth/callback:
//   - /login is the explicit signed-out surface; re-triggering a silent
//     prompt=none check there causes a spinner-then-button flicker and can
//     loop with SsoCallback.onLoginRequired (which navigates back to /login).
//   - /auth/callback owns the explicit code-exchange flow; a parallel silent
//     check would race the SsoCallback component.
const SsoCheckGate: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const skip =
    location.pathname === '/auth/callback' || location.pathname === '/login';
  useSsoCheck({ skip });
  return <>{children}</>;
};

// Authenticated app routes
const AuthenticatedApp = () => (
  <AppLayout>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/projects/:id" element={<ProjectPage />} />
        <Route path="/projects/:id/edit" element={<EditProjectPage />} />
        <Route path="/touchpoints" element={<TouchpointsPage />} />
        <Route path="/touchpoints/new" element={<TouchpointWizard />} />
        <Route path="/journeys" element={<JourneysPage />} />
        <Route path="/journeys/new" element={<JourneyDesigner />} />
        <Route path="/journeys/:id" element={<JourneyDesigner />} />
        <Route path="/personas" element={<PersonasPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/optimization" element={<OptimizationPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  </AppLayout>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppProvider>
        <BrowserRouter>
          <AuthProvider>
            <SsoCheckGate>
              <SkipLink />
              <OfflineBanner />
              <ErrorBoundary>
                <Suspense fallback={<AuthLoader />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/auth/callback" element={<SsoCallbackRoute />} />
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute>
                          <AuthenticatedApp />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              <NotificationStack />
              <ReauthDialog />
            </SsoCheckGate>
          </AuthProvider>
        </BrowserRouter>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
